// TODO: Migration ot translatedCopy.js of raw strings
// TODO: Using generative voice service to enable generative at mount

import React, { useState, useEffect, useRef } from 'react';
import { getUserData } from '../../services/API';
import DialogDisplay from '../DialogDisplay/DialogDisplay';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import AmiraIconButton, { ICON_BUTTON_TYPES } from '../Global/AmiraIconButton2/AmiraIconButton2';
import useComprehensionInference from '../Reader/Phrase/hooks/useComprehensionInference';

const DEFAULT_MAX_NO_SUBMIT_TIME = 60000;
const DEFAULT_MINIMUM_SUBMIT_WAIT_TIME = 1000;
const CONFIGURED_GRADE_TIMERS = {
  K: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  1: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  2: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  3: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  4: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  5: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  6: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
}
const MAX_ATTEMPTS_PER_WORD = 2; 

const Retell = ({ isInstruction, videoURL, speech, onDone, storyId, activityId, phraseIndex }) => {
  // State
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
  const [repeatButtonEnabled, setRepeatButtonEnabled] = useState(false);
  const [showCheckmarkOverlay, setShowCheckmarkOverlay] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [submitTimerElapsed, setSubmitTimerElapsed] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const submitTimerIntervalRef = useRef(null);

  const { 
    startListening, 
    transcript,
    triggerInference,
  } = useComprehensionInference({ 
    storyId, 
    phraseIndex, 
    activityId, 
    speech,
  });

  useEffect(() => {
    return () => {
      if (submitTimerIntervalRef.current) clearInterval(submitTimerIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isInstruction) {
      speakIntroduction();
    }
  }, [isInstruction]);

  const getEnableSubmitTimeout = () => {
    const grade = getUserData().sessionData.grade;
    return CONFIGURED_GRADE_TIMERS[grade]?.MINIMUM_SUBMIT_WAIT_TIME || DEFAULT_MINIMUM_SUBMIT_WAIT_TIME;
  };

  const getMaxNoSubmitTimeout = () => {
    const grade = getUserData().sessionData.grade;
    return CONFIGURED_GRADE_TIMERS[grade]?.MAX_NO_SUBMIT_TIME || DEFAULT_MAX_NO_SUBMIT_TIME;
  };

  const speakIntroduction = () => {
    setShowVideo(true);
    speech.speakWithPromise({ 
      textToSpeak: "Great job on that part. Now we’re going to try something else. My friend is going to read you a story. Let’s listen carefully together. After the story, I’ll ask you to tell me what happened in the story.", 
      useGenerative: true 
    }).finally(() => {
      setVideoPlaying(true);
    });
  };

  const speakDialogInstructions = () => {
    setShowVideo(false);
    setShowDialog(true);
    speech.speakWithPromise({ 
      textToSpeak: "Now I want you to tell me about the story we just heard.",
      useGenerative: true 
    }).finally(() => speakDialogQuestion());
  };

  const speakDialogQuestion = () => {
    setIsListening(false);
    speech.speakWithPromise({ 
      textToSpeak: "What happened in the story?",
      useGenerative: true 
    }).finally(() => {
      setIsListening(true);
      setRepeatButtonEnabled(true);
      setAttemptCount(attemptCount + 1);
      startListening(
        `retell/calibration/${storyId}`
      );
      startSubmitTimer();
    });
  };

  const startSubmitTimer = () => {
    if (submitTimerIntervalRef.current) clearInterval(submitTimerIntervalRef.current);

    setSubmitTimerElapsed(0); 
    const enableSubmitTimeout = getEnableSubmitTimeout();
    const maxNoSubmitTimeout = getMaxNoSubmitTimeout();

    submitTimerIntervalRef.current = setInterval(() => {
      let prevElapsedTime;
      let newElapsedTime;

      setSubmitTimerElapsed(prev => {
        prevElapsedTime = prev;
        newElapsedTime = prevElapsedTime + 250;
        return newElapsedTime;
      });

      if (prevElapsedTime <= enableSubmitTimeout && newElapsedTime >= enableSubmitTimeout) {
        setSubmitButtonEnabled(true);
      } else if (newElapsedTime > maxNoSubmitTimeout) {
        setSubmitTimerElapsed(maxNoSubmitTimeout);
        handleSubmit();
      }
    }, 250);
  }

  const onAcceptAnswer = () => {
    speech.speakWithPromise({ 
      textToSpeak: "Thanks for telling me about the story! Let’s move on to the next part.",
      useGenerative: true
    }).finally(() => {
      setShowCheckmarkOverlay(true);
      if (onDone) onDone();
    });
  }

  const onRejectAnswer = () => {
    speech.speakWithPromise({ 
      textToSpeak: "Thanks, but could you tell me more about the story?",
      useGenerative: true
    }).finally(() => speakDialogQuestion());
  }

  const onInference = (data) => {
    const { prompt } = data;

    if (prompt === "Great job." || currentAttemptCount >= MAX_ATTEMPTS_PER_WORD) {
      onAcceptAnswer();
    } else {
      onRejectAnswer();
    }
  }

  const onInferenceError = (error) => {
    console.error("Retell: onInferenceError", error);
    onAcceptAnswer();
  }

  const handleSubmit = () => {
    if (submitTimerIntervalRef.current) clearInterval(submitTimerIntervalRef.current);
    setIsListening(false);
    setSubmitButtonEnabled(false);
    setRepeatButtonEnabled(false);
    triggerInference({
      stage: 0,
      prompt: "What happened in the story?",
      storyId,
      phraseIndex,
    }, onInference, onInferenceError);
  };

  // Render
  return (
    <div className="retell">
      {showVideo && (
        <VideoPlayer
          videoURL={videoURL}
          text={"Retell Video"}
          unmountWhenDone={getUserData().sessionData.isiPad}
          key="theOnlyRetellVideo"
          speech={speech}
          assessment={true}
          videoPlaying={videoPlaying}
          // Show the overlay to continue after the video is done
          allowReplay={true}
          maxReplays={5}
          onDone={() => {
            setVideoPlaying(false);
          }}
          onNext={() => {
            speakDialogInstructions();
          }}
          useButtonLabels={true}
        />
      )}
      {showDialog && (
        <>
          <DialogDisplay
            // Enabled States
            submitButtonEnabled={submitButtonEnabled} 
            repeatButtonEnabled={repeatButtonEnabled} 
            // Visibility States
            showRepeatButton={true}
            showMicrophone={true}
            showPauseOverlay={!isListening}
            showCheckmarkOverlay={showCheckmarkOverlay}
            showTimer={true}
            showStudentTurn={true}
            showPrompt={true}
            // Interaction Handlers
            onClickRepeat={() => {
              clearInterval(submitTimerIntervalRef.current);
              setSubmitTimerElapsed(0);
              setSubmitButtonEnabled(false);
              setRepeatButtonEnabled(false);
              setIsListening(false);
              speakDialogInstructions();
            }}
            // Misc
            fakeActiveListeningStatus={isListening}
            volumeLevel={volumeLevel}
            transcript={transcript}
            submitTimerElapsed={submitTimerElapsed}
            getNoSubmitTimeout={getMaxNoSubmitTimeout}
            promptText={"What happened in the story?"}
            assessment={true}
          />
          <AmiraIconButton 
            type={ICON_BUTTON_TYPES.SUBMIT}
            oblong
            onClick={handleSubmit}
            disabled={!submitButtonEnabled}
            assessment={true}
          />
        </>
      )}  
    </div>
  );


};

export default Retell;